class ScrollLock extends HTMLElement {
  static lockCount = 0;
  static scrollY = 0;
  static isLocked = false;
  static scrollbarWidth = 0;
  static safetyWatcher = null;
  static viewportWatcher = null;
  static scrollCorrectionInterval = null;
  static scrollCorrectionRaf = null;

  connectedCallback() {
    if (this.hasAttribute("active")) {
      this.lock();
    }
    
    // Note: We rely on attributeChangedCallback for attribute changes
    // MutationObserver was causing duplicate calls with attributeChangedCallback
  }
  
  disconnectedCallback() {
    // Clean up any remaining locks when element is removed
    this.stopSafetyWatcher();
    this.stopViewportWatcher();
    if (ScrollLock.lockCount > 0) {
      this.forceUnlock();
    }
  }

  static get observedAttributes() {
    return ["active"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "active") {
      // Use setTimeout to ensure the attribute change has been processed
      setTimeout(() => {
        if (this.hasAttribute("active")) {
          this.lock();
        } else {
          this.unlock();
        }
      }, 0);
    }
  }

  getScrollbarWidth() {
      // Return cached value if already calculated
      if (ScrollLock.scrollbarWidth !== undefined && ScrollLock.scrollbarWidth !== 0) {
        return ScrollLock.scrollbarWidth;
      }
  
      // Check if body exists (in case this is called very early)
      if (!document.body) {
        return 0;
      }
  
      // Create a temporary div to measure scrollbar width
      const outer = document.createElement('div');
      outer.style.cssText = 'visibility: hidden; position: absolute; top: -9999px; width: 100px; height: 100px; overflow: scroll; msOverflowStyle: scrollbar;';
      document.body.appendChild(outer);
  
      // Add inner div with content larger than outer to force scrollbar
      const inner = document.createElement('div');
      inner.style.cssText = 'width: 100%; height: 200px;';
      outer.appendChild(inner);
  
      // The scrollbar width is the difference between offsetWidth and clientWidth
      const scrollbarWidth = outer.offsetWidth - outer.clientWidth;
  
      outer.parentNode.removeChild(outer);
  
      return scrollbarWidth;
  }

  lock() {
    // Increment the lock count attribute
    const currentCount = parseInt(this.getAttribute('data-lock-count') || '0');
    this.setAttribute('data-lock-count', currentCount + 1);
    
    // If already locked, don't apply styles again
    if (ScrollLock.isLocked) {
      return;
    }
    
    // Only apply scroll lock styles when count goes from 0 to 1
    if (currentCount === 0) {
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      const isiOS = navigator.maxTouchPoints > 0;

      if (isiOS) {
        ScrollLock.scrollY = window.scrollY;
        
        // Get current viewport height (accounting for toolbar)
        const viewportHeight = window.visualViewport 
          ? window.visualViewport.height 
          : window.innerHeight;
        
        // Use overflow hidden - this works with headspace
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        
        // Store original scroll position to maintain it
        // We'll use scrollTo to maintain position rather than constraining height
        // This prevents the page from jumping while still locking scroll
        window.scrollTo(0, ScrollLock.scrollY);
        
        // Prevent pull-to-refresh and other touch gestures
        document.body.style.touchAction = "none";
        document.body.style.overscrollBehavior = "none";
        
        // Add touch event listeners to prevent scrolling
        this.addTouchListeners();
        
        // Watch for viewport changes (when iOS toolbar collapses/expands)
        this.startViewportWatcher();
        
        // Add a scroll listener to immediately correct any scroll changes
        this.addScrollListener();
      } else {
        // Calculate scrollbar width before hiding it
        ScrollLock.scrollbarWidth = this.getScrollbarWidth();
        
        // Apply padding to body to prevent layout shift
        if (ScrollLock.scrollbarWidth > 0) {
          document.body.style.paddingRight = `${ScrollLock.scrollbarWidth}px`;
          // Store scrollbar width as CSS variable for use in stylesheets
          document.documentElement.style.setProperty('--scrollbar-width', `${ScrollLock.scrollbarWidth}px`);
        }
        
        // Add class to body for custom styling
        document.body.classList.add('scroll-locked');
        document.body.style.overflow = "hidden";
      }
      ScrollLock.isLocked = true;
    }
    
    // Start safety watcher when first lock is applied
    this.startSafetyWatcher();
  }

  unlock() {
    // Decrement the lock count attribute
    const currentCount = parseInt(this.getAttribute('data-lock-count') || '0');
    const newCount = Math.max(currentCount - 1, 0);
    this.setAttribute('data-lock-count', newCount);
    
    // Only unlock when count reaches 0
    if (newCount === 0) {
      ScrollLock.isLocked = false;
      const isiOS = /iP(ad|hone|od)/.test(navigator.userAgent || navigator.vendor || window.opera);

      if (isiOS) {
        // Stop viewport watcher
        this.stopViewportWatcher();
        
        // Remove scroll listener
        this.removeScrollListener();
        
        // Restore html and body styles
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
        document.body.style.touchAction = "";
        document.body.style.overscrollBehavior = "";
        window.scrollTo(0, ScrollLock.scrollY || 0);
        
        // Remove touch event listeners
        this.removeTouchListeners();
      } else {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
        document.documentElement.style.removeProperty('--scrollbar-width');
        document.body.classList.remove('scroll-locked');
        ScrollLock.scrollbarWidth = 0;
      }
      
      // Stop safety watcher when fully unlocked
      this.stopSafetyWatcher();
    }
  }

  addTouchListeners() {
    // Store the bound functions so we can remove them later
    this.touchStartHandler = this.handleTouchStart.bind(this);
    this.touchMoveHandler = this.handleTouchMove.bind(this);
    this.touchEndHandler = this.handleTouchEnd.bind(this);
    
    document.addEventListener('touchstart', this.touchStartHandler, { passive: true });
    document.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
    document.addEventListener('touchend', this.touchEndHandler, { passive: true });
  }

  removeTouchListeners() {
    if (this.touchStartHandler) {
      document.removeEventListener('touchstart', this.touchStartHandler);
      document.removeEventListener('touchmove', this.touchMoveHandler);
      document.removeEventListener('touchend', this.touchEndHandler);
      this.touchStartHandler = null;
      this.touchMoveHandler = null;
      this.touchEndHandler = null;
    }
  }

  addScrollListener() {
    // Store the bound function so we can remove it later
    this.scrollHandler = this.handleScroll.bind(this);
    // Use passive: true for better performance - we don't preventDefault anyway
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    document.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  removeScrollListener() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      document.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
    
    // Cancel any pending scroll correction
    if (ScrollLock.scrollCorrectionRaf) {
      cancelAnimationFrame(ScrollLock.scrollCorrectionRaf);
      ScrollLock.scrollCorrectionRaf = null;
    }
  }

  handleScroll(e) {
    if (!ScrollLock.isLocked) return;
    
    const targetScrollY = ScrollLock.scrollY || 0;
    
    // Throttle scroll corrections using requestAnimationFrame for performance
    // Scroll events can fire very frequently, so we don't want to call scrollTo() on every event
    if (Math.abs(window.scrollY - targetScrollY) > 1) {
      // Cancel any pending scroll correction
      if (ScrollLock.scrollCorrectionRaf) {
        cancelAnimationFrame(ScrollLock.scrollCorrectionRaf);
      }
      
      // Schedule correction for next animation frame
      ScrollLock.scrollCorrectionRaf = requestAnimationFrame(() => {
        if (!ScrollLock.isLocked) return;
        const currentScrollY = ScrollLock.scrollY || 0;
        if (Math.abs(window.scrollY - currentScrollY) > 1) {
          window.scrollTo(0, currentScrollY);
        }
        ScrollLock.scrollCorrectionRaf = null;
      });
    }
  }

  handleTouchStart(e) {
    // Store the initial touch position and element
    this.startY = e.touches[0].clientY;
    this.startX = e.touches[0].clientX;
    this.startElement = e.target;
    this.isScrolling = false;
  }
  
  handleTouchEnd(e) {
    // Reset touch tracking
    this.startY = null;
    this.startX = null;
    this.startElement = null;
    this.isScrolling = false;
  }

  handleTouchMove(e) {
    // Allow scrolling on elements with data-scroll-lock-scrollable attribute
    const target = e.target;
    const scrollableElement = target.closest('[data-scroll-lock-scrollable]');
    
    // If the touch is within a scrollable element, allow it completely
    if (scrollableElement) {
      this.isScrolling = true;
      return;
    }
    
    // Check if the touch started within a scrollable element
    if (this.startElement) {
      const startScrollableElement = this.startElement.closest('[data-scroll-lock-scrollable]');
      if (startScrollableElement) {
        this.isScrolling = true;
        return;
      }
    }
    
    // Only prevent default for vertical scrolling on the body
    // This prevents pull-to-refresh and body scrolling while allowing horizontal gestures
    const touch = e.touches[0];
    const deltaY = Math.abs(touch.clientY - this.startY);
    const deltaX = Math.abs(touch.clientX - this.startX);
    
    // Only prevent if it's primarily a vertical scroll gesture and started outside scrollable areas
    if (deltaY > deltaX && deltaY > 10 && !this.isScrolling) {
      e.preventDefault();
    }
  }
  
  /**
   * Check the current state of the scroll lock
   */
  getState() {
    return {
      hasActiveAttribute: this.hasAttribute('active'),
      lockCount: parseInt(this.getAttribute('data-lock-count') || '0'),
      scrollY: ScrollLock.scrollY,
      bodyOverflow: document.body.style.overflow,
      bodyPosition: document.body.style.position,
      isLocked: ScrollLock.isLocked
    };
  }

  /**
   * Force unlock the scroll lock (useful for cleanup)
   */
  startSafetyWatcher() {
    // Clear any existing watcher
    this.stopSafetyWatcher();
    
    // Start a new watcher that checks every 100ms
    ScrollLock.safetyWatcher = setInterval(() => {
      const lockCount = parseInt(this.getAttribute('data-lock-count') || '0');
      
      // If count is 0 but page is still locked, force unlock after 1 second
      if (lockCount === 0 && ScrollLock.isLocked) {
        console.warn('ScrollLock: Safety mechanism triggered - force unlocking stuck scroll lock');
        this.forceUnlock();
      }
    }, 100);
  }
  
  stopSafetyWatcher() {
    if (ScrollLock.safetyWatcher) {
      clearInterval(ScrollLock.safetyWatcher);
      ScrollLock.safetyWatcher = null;
    }
  }

  startViewportWatcher() {
    // Clear any existing watcher
    this.stopViewportWatcher();
    
    // Handler to re-enforce scroll lock when viewport changes
    const handleViewportChange = () => {
      if (!ScrollLock.isLocked) return;
      
      const targetScrollY = ScrollLock.scrollY || 0;
      
      // Clear any existing scroll correction interval
      if (ScrollLock.scrollCorrectionInterval) {
        clearInterval(ScrollLock.scrollCorrectionInterval);
        ScrollLock.scrollCorrectionInterval = null;
      }
      
      // Aggressively maintain scroll position when toolbar collapses/expands
      // iOS allows scrolling during toolbar transitions, so we need to correct it multiple times
      const correctScroll = () => {
        if (!ScrollLock.isLocked) return;
        window.scrollTo(0, targetScrollY);
      };
      
      // Correct scroll position immediately
      correctScroll();
      
      // Continue checking for a short period to catch any delayed scrolling
      let attempts = 0;
      ScrollLock.scrollCorrectionInterval = setInterval(() => {
        attempts++;
        if (!ScrollLock.isLocked || attempts > 10) {
          clearInterval(ScrollLock.scrollCorrectionInterval);
          ScrollLock.scrollCorrectionInterval = null;
          return;
        }
        if (Math.abs(window.scrollY - targetScrollY) > 1) {
          window.scrollTo(0, targetScrollY);
        }
      }, 50);
    };
    
    // Use visualViewport API if available (better for iOS)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      window.visualViewport.addEventListener('scroll', handleViewportChange);
      ScrollLock.viewportWatcher = {
        type: 'visualViewport',
        handler: handleViewportChange
      };
    } else {
      // Fallback to window resize for older browsers
      window.addEventListener('resize', handleViewportChange);
      ScrollLock.viewportWatcher = {
        type: 'window',
        handler: handleViewportChange
      };
    }
  }
  
  stopViewportWatcher() {
    if (ScrollLock.viewportWatcher) {
      if (ScrollLock.viewportWatcher.type === 'visualViewport' && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', ScrollLock.viewportWatcher.handler);
        window.visualViewport.removeEventListener('scroll', ScrollLock.viewportWatcher.handler);
      } else if (ScrollLock.viewportWatcher.type === 'window') {
        window.removeEventListener('resize', ScrollLock.viewportWatcher.handler);
      }
      ScrollLock.viewportWatcher = null;
    }
    
    // Clear any scroll correction interval
    if (ScrollLock.scrollCorrectionInterval) {
      clearInterval(ScrollLock.scrollCorrectionInterval);
      ScrollLock.scrollCorrectionInterval = null;
    }
  }

  forceUnlock() {
    this.setAttribute('data-lock-count', '0');
    ScrollLock.isLocked = false;
    this.stopSafetyWatcher();
    this.stopViewportWatcher();
    const isiOS = /iP(ad|hone|od)/.test(navigator.userAgent || navigator.vendor || window.opera);
    
    if (isiOS) {
      // Remove scroll listener
      this.removeScrollListener();
      
      // Restore html and body styles
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
      document.body.style.overscrollBehavior = "";
      window.scrollTo(0, ScrollLock.scrollY || 0);
      
      // Remove touch event listeners
      this.removeTouchListeners();
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.removeProperty('--scrollbar-width');
      document.body.classList.remove('scroll-locked');
      ScrollLock.scrollbarWidth = 0;
    }
  }
}

customElements.define("scroll-lock", ScrollLock);   